'use client';

import { faSliders } from '@fortawesome/pro-light-svg-icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { useIsMobile } from '@shared/hooks/useMobile';
import { cn } from '@shared/utils/utils';
import { useRef } from 'react';
import type { FiltersDropdownProps } from './types';

/**
 * FiltersDropdown - Contenedor reutilizable de filtros
 *
 * Muestra el panel de filtros en Dropdown (desktop) o Modal (mobile).
 * Usar desde Orders, Locations, Merchandise o cualquier módulo que necesite
 * el mismo patrón: botón "Filtros" + contenido personalizado.
 *
 * Características:
 * - Desktop: DropdownMenu con título y children
 * - Mobile: ModalCenter con título y children (abre con ref)
 * - Badge opcional con contador de filtros activos
 * - Botón trigger con icono faSliders y etiqueta configurable
 */
export const FiltersDropdown = ({
  title,
  children,
  activeFiltersCount = 0,
  activeFiltersAriaLabel,
  triggerIcon = faSliders,
  dropdownContentClassName = 'sm:min-w-[700px] sm:max-w-[700px] p-4 bg-body-bg text-foreground border border-card-border',
  triggerLabel,
  triggerTestId = 'filters-toggle-button',
}: FiltersDropdownProps) => {
  const isMobile = useIsMobile();
  const modalRef = useRef<ModalCenterRef>(null);

  const displayLabel = triggerLabel ?? title;

  /* Trigger button - Botón que abre filtros (dropdown o modal) */
  const triggerButton = (
    <div className="relative">
      <Button
        variant="outline"
        type="button"
        size="sm"
        icon={triggerIcon}
        iconPosition="start"
        className={cn(
          'shadow-none transition-colors border border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground hover:border-input',
        )}
        data-testid={triggerTestId}
        onClick={isMobile ? () => modalRef.current?.openModal() : undefined}
      >
        <span className="hidden sm:block text-sm">{displayLabel}</span>
      </Button>
      {/* Badge de contador de filtros activos */}
      {activeFiltersCount > 0 && (
        <span
          className="absolute -top-2 -right-2 bg-primary text-btn-text text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm z-10"
          aria-label={activeFiltersAriaLabel ?? `${activeFiltersCount} filtros activos`}
        >
          {activeFiltersCount}
        </span>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {triggerButton}
        <ModalCenter
          ref={modalRef}
          title={title}
          type="default"
          width="xl"
          maxHeight="min(90vh, 720px)"
          className="w-full max-w-none"
          classNameBody="min-h-0 overflow-y-auto overscroll-contain p-3 pt-0"
        >
          {children}
        </ModalCenter>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className={cn(dropdownContentClassName, 'flex flex-col overflow-hidden')}
      >
        <div className="flex shrink-0 items-center justify-between mb-2 pt-1">
          <div className="text-sm font-semibold text-foreground">{title}</div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">{children}</div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
